// @flow
import {
  adjust,
  map,
  evolve,
  assoc,
  propEq,
  compose,
  uniq,
  without,
  pathOr,
} from 'ramda';

import { Products } from '../../../shared-queries/Products.graphql';
import { ProductsCounters } from '../../../shared-queries/ProductsCounters.graphql';
import {
  changesVar,
  changeSuccessIdsVar,
  changeErrorIdsVar,
} from '../../../utils/apollo-cache';

import type { Products_products_nodes as ProductT } from '../../../typings/product-query.flow';
import type { ProductChange } from '../../../typings/product';
import type { Cache } from '../../../typings/apollo';

const getProductId = (change) => {
  const { productId } = change;
  return productId === 'mass' ? 'all' : productId;
};

const skipChange = (change, shouldUseMassUpdate, products) =>
  !change ||
  change.ignore ||
  (!shouldUseMassUpdate && change.productId === 'mass') ||
  !products;

type Reducer<R, V> = (r: R, v?: V) => R;

export const getAllChangedProducts = (changes: ProductChange[]): number => {
  const ids = {};
  return changes.reduce((result, c) => {
    if (c.ignore || c.productId === 'mass') return result;
    const total = ids[c.productId] ? result : result + 1;
    ids[c.productId] = true;
    return total;
  }, 0);
};

const updateChanges =
  (
    fn: (change: ProductChange, currentChange: any) => ProductChange,
    test: (change: ProductChange) => boolean,
    shouldUseMassUpdate: boolean,
    products: ProductT[]
  ): any =>
  (
    prev: Reducer<
      { [string]: { id: string, listings: Array<{}>, variations: Array<{}> } },
      ProductChange
    >
  ) =>
  (acc, change) => {
    const prevAcc = prev(acc, change);
    if (
      !test(change) ||
      skipChange(change, shouldUseMassUpdate, products) ||
      !change
    ) {
      return prevAcc;
    }
    const pId = getProductId(change);
    const currentChange = prevAcc[pId] || {
      id: pId,
      listings: [],
      variations: [],
    };
    return assoc(pId, fn(change, currentChange), prevAcc);
  };

export const formatChanges = (
  changes: ProductChange[],
  products: ProductT[],
  activeShowcasesIds: string[],
  shouldUseMassUpdate: boolean,
  filters: any
): {
  formattedChanges: mixed[],
  showcasesToTurnOn: string[],
  publishOnDisabledShowcasesCount: number,
  hasPublish: boolean,
  restorePublishCount: number,
  restorePublishTitle: string,
} => {
  const showcasesToTurnOn: string[] = [];
  const publishOnDisabledShowcases = [];
  let hasPublish = false;
  let restorePublishIds = [];
  let restorePublishTitle = '';

  const result = Object.values(
    changes.reduce(
      compose(
        updateChanges(
          (change, currentChange) => {
            const pId = getProductId(change);
            const { variationId, value } = change;

            if (pId === 'all') {
              return {
                ...currentChange,
                variations: currentChange.variations.concat({
                  id: 'all',
                  quantity: parseInt(value, 10),
                }),
              };
            }
            let vId = variationId;
            if (!variationId) {
              const product = products.find((p) => p && p.id === pId);
              vId = product ? product.variations[0].id : '';
            }

            return {
              ...currentChange,
              variations: currentChange.variations.concat({
                id: vId,
                quantity: parseInt(value, 10),
              }),
            };
          },
          propEq('fieldName', 'quantity'),
          shouldUseMassUpdate,
          products
        ),
        updateChanges(
          (change, currentChange) => {
            const pId = getProductId(change);
            const { fieldName, showcaseId, value } = change;

            if (value && !activeShowcasesIds.includes(showcaseId)) {
              if (showcaseId !== null && showcaseId !== undefined) {
                showcasesToTurnOn.push(showcaseId);
              }
              publishOnDisabledShowcases.push(pId);
            }

            if (!hasPublish && fieldName === 'selected' && value) {
              hasPublish = true;
            }

            let archived;

            if (pId === 'all') {
              const listingChange = {
                id: 'all',
                selected: value,
                showcaseId,
              };

              if (filters.archived && fieldName === 'selected' && value) {
                restorePublishIds.push('all');
                restorePublishTitle = 'all';
                archived = false;
              }

              return {
                ...currentChange,
                archived,
                listings: currentChange.listings.concat(listingChange),
              };
            }
            const product = products.find((p) => p && p.id === pId);
            if (
              product &&
              product.archived &&
              fieldName === 'selected' &&
              value
            ) {
              restorePublishIds.push(product.id);
              restorePublishTitle = product.title || '';
              archived = false;
            }

            if (product && product.listings) {
              const listing =
                product.listings.find(
                  (l) => l.showcase && l.showcase.id === showcaseId
                ) || {};

              const listingChange = {
                id: listing.id,
                selected: value,
              };

              return {
                ...currentChange,
                archived,
                listings: currentChange.listings.concat(listingChange),
              };
            }

            return currentChange;
          },
          propEq('fieldName', 'selected'),
          shouldUseMassUpdate,
          products
        ),
        updateChanges(
          (change, currentChange): any =>
            assoc(change.fieldName, change.value, currentChange),
          propEq('fieldName', 'shippingProfileId'),
          shouldUseMassUpdate,
          products
        )
      )((r) => r),
      {}
    )
  );

  restorePublishIds = uniq(restorePublishIds);

  return {
    formattedChanges: result,
    showcasesToTurnOn: uniq(showcasesToTurnOn),
    publishOnDisabledShowcasesCount: uniq(publishOnDisabledShowcases).length,
    hasPublish,
    restorePublishCount: restorePublishIds.length,
    restorePublishTitle: restorePublishIds.some((id) => id === 'all')
      ? 'all'
      : restorePublishTitle,
  };
};

export const updateCache = (
  client: Cache,
  {
    data: {
      productMassUpdate: { success },
    },
  }: { data: { productMassUpdate: { success: boolean } } },
  changes: ProductChange[],
  changeSuccessIds: string[],
  changeErrorIds: string[],
  filters: any
) => {
  const productsQuery = client.readQuery({
    query: Products,
    variables: {
      filters,
    },
  });
  let updatedProducts = productsQuery;
  let restorePublishIds = [];

  if (success) {
    updatedProducts = evolve(
      {
        products: {
          nodes: (nodes) =>
            changes.reduce((result, change) => {
              const index = nodes.findIndex(
                (node) => node.id === change.productId
              );

              if (change.ignore || index < 0) {
                return result;
              }

              return adjust(
                index,
                (node) => {
                  let republishingProps = {};
                  if (change.fieldName !== 'selected') {
                    // Imitate republishing immediately, as it has small lag on server
                    republishingProps = {
                      problems: [],
                      selected: true,
                      published: false,
                    };
                  }

                  if (change.fieldName === 'quantity') {
                    const vIndex = change.variationId
                      ? node.variations.findIndex(
                          (v) => v.id === change.variationId
                        )
                      : 0;

                    changeSuccessIdsVar([...changeSuccessIds, node.id]);
                    changeErrorIdsVar(without([node.id], changeErrorIds));

                    return {
                      ...node,
                      totalQuantity: change.value,
                      variations:
                        vIndex < 0
                          ? node.variations
                          : adjust(
                              vIndex,
                              assoc('quantity', change.value),
                              node.variations
                            ),
                      listings: node.listings.map((l) => ({
                        ...l,
                        ...(l.managed &&
                        ((l.problems && l.problems.length) || l.published)
                          ? republishingProps
                          : {}),
                      })),
                    };
                  }

                  if (change.fieldName === 'selected') {
                    let archived;
                    const lIndex = node.listings.findIndex(
                      (l) => l.showcase.id === change.showcaseId
                    );

                    changeSuccessIdsVar([...changeSuccessIds, node.id]);
                    changeErrorIdsVar(without([node.id], changeErrorIds));

                    if (node && node.archived && change.value) {
                      restorePublishIds.push(node.id);
                      archived = false;
                    }

                    return {
                      ...node,
                      archived,
                      listings:
                        lIndex < 0
                          ? node.listings
                          : adjust(
                              lIndex,
                              assoc('selected', change.value),
                              node.listings
                            ),
                    };
                  }

                  changeSuccessIdsVar([...changeSuccessIds, node.id]);
                  changeErrorIdsVar(without([node.id], changeErrorIds));

                  return {
                    ...node,
                    [change.fieldName]: change.value,
                    listings: node.listings.map((l) => ({
                      ...l,
                      ...(l.managed &&
                      ((l.problems && l.problems.length) || l.published)
                        ? republishingProps
                        : {}),
                    })),
                  };
                },
                result
              );
            }, nodes),
        },
      },
      productsQuery
    );

    restorePublishIds = uniq(restorePublishIds);

    if (filters.archived && restorePublishIds.length) {
      const productsCountersQuery = client.readQuery({
        query: ProductsCounters,
        variables: {
          filters,
        },
      });

      updatedProducts = evolve(
        {
          products: {
            nodes: (nodes) => nodes.filter((n) => n.archived),
          },
        },
        updatedProducts
      );

      const notCheckedCount =
        pathOr([], ['products', 'nodes'], productsQuery).length -
        restorePublishIds.length;
      const newCount = notCheckedCount < 0 ? 0 : notCheckedCount;

      client.writeQuery({
        query: ProductsCounters,
        variables: {
          filters,
        },
        data: evolve(
          {
            products: {
              totalCount: (value) =>
                restorePublishIds.some((id) => id === 'all')
                  ? newCount
                  : value - restorePublishIds.length,
            },
          },
          productsCountersQuery
        ),
      });
    }

    changesVar([]);
    client.writeQuery({
      query: Products,
      data: updatedProducts,
      variables: {
        filters,
      },
    });
  } else {
    const nodes = pathOr([], ['products', 'nodes'], productsQuery);
    changes.forEach((change) => {
      const index = nodes.findIndex((node) => node.id === change.productId);

      if (change.ignore || index < 0) {
        return;
      }

      changeSuccessIdsVar(without([change.productId], changeSuccessIds));
      changeErrorIdsVar([...changeErrorIds, change.productId]);
    });
  }
};

export const clearRestoreSelectChanges = (
  changes: ProductChange[],
  filters: any
) => {
  changesVar(
    map(
      (change) =>
        assoc(
          'ignore',
          change.ignore ||
            (change.fieldName === 'selected' &&
              change.value &&
              filters.archived),
          change
        ),
      changes
    )
  );
};
